import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { PanelDataItem, SyncSourceResult } from '@/types/Dashboards.types'
import type { RootState } from '@/redux'

interface DashboardSyncState {
  isSyncing: boolean
  syncError: string | null
  lastSyncedAt: string | null
  panelData: Record<string, PanelDataItem>
  isPanelDataLoading: boolean
  panelDataError: string | null
  lastSyncResults: SyncSourceResult[]
}

const initialState: DashboardSyncState = {
  isSyncing: false,
  syncError: null,
  lastSyncedAt: null,
  panelData: {},
  isPanelDataLoading: false,
  panelDataError: null,
  lastSyncResults: [],
}

const dashboardSyncSlice = createSlice({
  name: 'dashboardSync',
  initialState,
  reducers: {
    setSyncing(state, action: PayloadAction<boolean>) {
      state.isSyncing = action.payload
      // NOTE: does NOT clear syncError. syncError is cleared only when
      // setSyncError(null) is explicitly dispatched (at triggerSync start).
    },

    setSyncError(state, action: PayloadAction<string | null>) {
      state.syncError = action.payload
    },

    setPanelData(state, action: PayloadAction<PanelDataItem[]>) {
      // Replace panelData keyed by panelId
      state.panelData = {}
      for (const item of action.payload) {
        state.panelData[item.panelId] = item
      }

      // Compute lastSyncedAt as the oldest non-null lastSyncedAt (most conservative).
      // Filter out NaN timestamps to guard against invalid date strings from the API.
      const allDates = action.payload
        .map(p => p.lastSyncedAt)
        .filter((d): d is string => d !== null)
        .map(d => new Date(d).getTime())
        .filter(t => Number.isFinite(t))

      state.lastSyncedAt =
        allDates.length > 0 ? new Date(Math.min(...allDates)).toISOString() : null
    },

    setIsPanelDataLoading(state, action: PayloadAction<boolean>) {
      state.isPanelDataLoading = action.payload
    },

    setPanelDataError(state, action: PayloadAction<string | null>) {
      state.panelDataError = action.payload
    },

    setLastSyncResults(state, action: PayloadAction<SyncSourceResult[]>) {
      state.lastSyncResults = action.payload
    },

    clearSync() {
      return initialState
    },
  },
})

export const dashboardSyncReducer = dashboardSyncSlice.reducer
export const dashboardSyncActions = dashboardSyncSlice.actions

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectIsSyncing = (state: RootState): boolean =>
  state.dashboardSync.isSyncing

/** Consumed by Canvas (error banner), NOT by SyncButton. */
export const selectSyncError = (state: RootState): string | null =>
  state.dashboardSync.syncError

export const selectLastSyncedAt = (state: RootState): string | null =>
  state.dashboardSync.lastSyncedAt

export const selectPanelData = (state: RootState): Record<string, PanelDataItem> =>
  state.dashboardSync.panelData

export const selectIsPanelDataLoading = (state: RootState): boolean =>
  state.dashboardSync.isPanelDataLoading

export const selectLastSyncResults = (state: RootState): SyncSourceResult[] =>
  state.dashboardSync.lastSyncResults

export const selectPanelDataError = (state: RootState): string | null =>
  state.dashboardSync.panelDataError

export default dashboardSyncSlice
